export type AuditSeverity = 'info' | 'warning' | 'critical';

export type GlobalAuditAction =
  | 'login'
  | 'logout'
  | 'create'
  | 'edit'
  | 'delete'
  | 'approve'
  | 'reject'
  | 'permission_change'
  | 'config_change'
  | 'critical_error'
  | 'plan_change'
  | 'feature_flag'
  | 'export'
  | 'bulk_operation';

export type GlobalAuditModule =
  | 'auth'
  | 'radios'
  | 'plans'
  | 'frequencies'
  | 'moderation'
  | 'settings'
  | 'content'
  | 'billing'
  | 'integrations'
  | 'security'
  | 'storage'
  | 'feature_flags'
  | 'api'
  | 'notifications';

export interface GlobalAuditEntry {
  id: number;
  userId: string;
  userName: string;
  userRole: string;
  action: GlobalAuditAction;
  module: GlobalAuditModule;
  targetName: string;
  targetId: string;
  date: string;
  result: 'success' | 'failure' | 'pending';
  severity: AuditSeverity;
  details: string;
  ipAddress: string;
  country: string;
  city: string;
  device: string;
  browser: string;
  os: string;
  sessionId: string;
}

export const actionLabels: Record<GlobalAuditAction, string> = {
  login: 'Inicio de sesión',
  logout: 'Cierre de sesión',
  create: 'Creación',
  edit: 'Edición',
  delete: 'Eliminación',
  approve: 'Aprobación',
  reject: 'Rechazo',
  permission_change: 'Cambio de permisos',
  config_change: 'Cambio de configuración',
  critical_error: 'Error crítico',
  plan_change: 'Cambio de plan',
  feature_flag: 'Feature Flag',
  export: 'Exportación',
  bulk_operation: 'Operación masiva',
};

export const moduleLabels: Record<GlobalAuditModule, string> = {
  auth: 'Autenticación',
  radios: 'Radios',
  plans: 'Planes',
  frequencies: 'Frecuencias',
  moderation: 'Moderación',
  settings: 'Configuración',
  content: 'Contenido',
  billing: 'Facturación',
  integrations: 'Integraciones',
  security: 'Seguridad',
  storage: 'Almacenamiento',
  feature_flags: 'Feature Flags',
  api: 'API',
  notifications: 'Notificaciones',
};

export const moduleIcons: Record<GlobalAuditModule, string> = {
  auth: 'ri-shield-keyhole-line',
  radios: 'ri-radio-line',
  plans: 'ri-stack-line',
  frequencies: 'ri-radar-line',
  moderation: 'ri-shield-check-line',
  settings: 'ri-settings-3-line',
  content: 'ri-file-list-3-line',
  billing: 'ri-bank-card-line',
  integrations: 'ri-plug-line',
  security: 'ri-lock-line',
  storage: 'ri-hard-drive-2-line',
  feature_flags: 'ri-flag-line',
  api: 'ri-terminal-box-line',
  notifications: 'ri-notification-3-line',
};

export const severityLabels: Record<AuditSeverity, string> = {
  info: 'Informativo',
  warning: 'Advertencia',
  critical: 'Crítico',
};

export const globalAuditEntries: GlobalAuditEntry[] = [
  {
    id: 1,
    userId: 'SA-001',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    action: 'login',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 07:45:22',
    result: 'success',
    severity: 'info',
    details: 'Inicio de sesión exitoso con verificación 2FA completada. Ubicación reconocida.',
    ipAddress: '190.124.45.10',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 15.2',
    sessionId: 'SESS-A7F3B2',
  },
  {
    id: 2,
    userId: 'SA-002',
    userName: 'María Gutiérrez',
    userRole: 'Admin Senior',
    action: 'login',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 08:10:05',
    result: 'success',
    severity: 'info',
    details: 'Inicio de sesión exitoso. Verificación biométrica completada.',
    ipAddress: '190.124.45.22',
    country: 'Argentina',
    city: 'Córdoba',
    device: 'Desktop',
    browser: 'Firefox 132',
    os: 'Windows 11',
    sessionId: 'SESS-C4D9E1',
  },
  {
    id: 3,
    userId: 'SA-005',
    userName: 'Luis Hernández',
    userRole: 'Admin',
    action: 'login',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 08:12:48',
    result: 'failure',
    severity: 'warning',
    details: 'Intento de inicio de sesión fallido. Contraseña incorrecta. Intento 1 de 5.',
    ipAddress: '201.187.92.14',
    country: 'México',
    city: 'Guadalajara',
    device: 'Mobile',
    browser: 'Chrome 126',
    os: 'Android 14',
    sessionId: '—',
  },
  {
    id: 4,
    userId: 'SA-005',
    userName: 'Luis Hernández',
    userRole: 'Admin',
    action: 'login',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 08:13:15',
    result: 'success',
    severity: 'info',
    details: 'Inicio de sesión exitoso después de reintento.',
    ipAddress: '201.187.92.14',
    country: 'México',
    city: 'Guadalajara',
    device: 'Mobile',
    browser: 'Chrome 126',
    os: 'Android 14',
    sessionId: 'SESS-F2A8D4',
  },
  {
    id: 5,
    userId: 'SA-001',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    action: 'approve',
    module: 'radios',
    targetName: 'Radio Vibra FM (90.80 RFW)',
    targetId: 'RAD-015',
    date: '2026-07-12 08:25:40',
    result: 'success',
    severity: 'info',
    details: 'Aprobó solicitud de registro. Plan asignado: Premium. Stream URL verificada. Frecuencia asignada: 90.80 RFW.',
    ipAddress: '190.124.45.10',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 15.2',
    sessionId: 'SESS-A7F3B2',
  },
  {
    id: 6,
    userId: 'SA-002',
    userName: 'María Gutiérrez',
    userRole: 'Admin Senior',
    action: 'plan_change',
    module: 'plans',
    targetName: 'Radio Élite Digital (91.30 RFW)',
    targetId: 'RAD-004',
    date: '2026-07-12 08:50:33',
    result: 'success',
    severity: 'info',
    details: 'Cambió plan de Free a Pro. Nuevo precio: $19/mes. Storage: 50 GB. Usuarios: hasta 5. Marketplace habilitado.',
    ipAddress: '190.124.45.22',
    country: 'Argentina',
    city: 'Córdoba',
    device: 'Desktop',
    browser: 'Firefox 132',
    os: 'Windows 11',
    sessionId: 'SESS-C4D9E1',
  },
  {
    id: 7,
    userId: 'SA-001',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    action: 'reject',
    module: 'radios',
    targetName: 'Radio XYZ Digital (102.50 RFW)',
    targetId: 'RAD-099',
    date: '2026-07-12 09:05:11',
    result: 'success',
    severity: 'warning',
    details: 'Rechazó solicitud de registro. Motivo: documentación incompleta. Stream URL no funcional. Logo excede tamaño máximo.',
    ipAddress: '190.124.45.10',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 15.2',
    sessionId: 'SESS-A7F3B2',
  },
  {
    id: 8,
    userId: 'SA-003',
    userName: 'Andrés López',
    userRole: 'Moderador',
    action: 'delete',
    module: 'moderation',
    targetName: 'Mensaje ofensivo en Cabina Radio Actitud',
    targetId: 'MSG-8742',
    date: '2026-07-12 09:22:18',
    result: 'success',
    severity: 'warning',
    details: 'Eliminó mensaje por lenguaje inapropiado. Usuario @troll_user notificado. Strike #2 acumulado.',
    ipAddress: '190.124.45.35',
    country: 'Argentina',
    city: 'Rosario',
    device: 'Desktop',
    browser: 'Edge 126',
    os: 'Windows 10',
    sessionId: 'SESS-B3E7C9',
  },
  {
    id: 9,
    userId: 'SA-002',
    userName: 'María Gutiérrez',
    userRole: 'Admin Senior',
    action: 'config_change',
    module: 'frequencies',
    targetName: 'TechBeats (88.50 → 88.70 RFW)',
    targetId: 'RAD-003',
    date: '2026-07-12 09:45:50',
    result: 'success',
    severity: 'info',
    details: 'Reasignó frecuencia por conflicto de dial detectado. Nueva frecuencia: 88.70 RFW. Notificación enviada al propietario.',
    ipAddress: '190.124.45.22',
    country: 'Argentina',
    city: 'Córdoba',
    device: 'Desktop',
    browser: 'Firefox 132',
    os: 'Windows 11',
    sessionId: 'SESS-C4D9E1',
  },
  {
    id: 10,
    userId: 'SA-004',
    userName: 'Diana Ríos',
    userRole: 'Admin',
    action: 'create',
    module: 'content',
    targetName: 'Nuevo artículo: Guía de Streaming',
    targetId: 'ART-2104',
    date: '2026-07-12 10:15:30',
    result: 'success',
    severity: 'info',
    details: 'Creó artículo de ayuda en Knowledge Base. Categoría: Streaming. Visibilidad: Pública.',
    ipAddress: '190.124.45.41',
    country: 'Colombia',
    city: 'Bogotá',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 14.6',
    sessionId: 'SESS-D1F5A8',
  },
  {
    id: 11,
    userId: 'SA-006',
    userName: 'Sofía Vargas',
    userRole: 'Moderador',
    action: 'login',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 10:30:00',
    result: 'failure',
    severity: 'critical',
    details: 'Intento de acceso desde IP no reconocida en país diferente al habitual. Bloqueo temporal de 30 minutos activado.',
    ipAddress: '87.221.45.90',
    country: 'España',
    city: 'Madrid',
    device: 'Mobile',
    browser: 'Safari 18',
    os: 'iOS 18.4',
    sessionId: '—',
  },
  {
    id: 12,
    userId: 'SA-001',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    action: 'feature_flag',
    module: 'feature_flags',
    targetName: 'ai_stream_optimizer',
    targetId: 'FF-0032',
    date: '2026-07-12 10:45:00',
    result: 'success',
    severity: 'info',
    details: 'Activó Feature Flag "AI Stream Optimizer" para el 20% de radios Premium. Rollout progresivo fase 1.',
    ipAddress: '190.124.45.10',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 15.2',
    sessionId: 'SESS-A7F3B2',
  },
  {
    id: 13,
    userId: 'SA-003',
    userName: 'Andrés López',
    userRole: 'Moderador',
    action: 'approve',
    module: 'moderation',
    targetName: 'Mensaje en Cabina Radio Urbana 101',
    targetId: 'MSG-11203',
    date: '2026-07-12 11:05:22',
    result: 'success',
    severity: 'info',
    details: 'Aprobó mensaje marcado por filtro automático. Falso positivo: mención de frecuencia era legítima. Ajustó sensibilidad del filtro.',
    ipAddress: '190.124.45.35',
    country: 'Argentina',
    city: 'Rosario',
    device: 'Desktop',
    browser: 'Edge 126',
    os: 'Windows 10',
    sessionId: 'SESS-B3E7C9',
  },
  {
    id: 14,
    userId: 'SA-002',
    userName: 'María Gutiérrez',
    userRole: 'Admin Senior',
    action: 'edit',
    module: 'plans',
    targetName: 'Plan Pro — Definición Global',
    targetId: 'PLAN-PRO',
    date: '2026-07-12 11:20:15',
    result: 'success',
    severity: 'warning',
    details: 'Editó definición de Plan Pro. Storage: 30→50 GB. Precio: $15→$19/mes. Bandwidth: ilimitado→500 GB. Afecta a 23 radios activas.',
    ipAddress: '190.124.45.22',
    country: 'Argentina',
    city: 'Córdoba',
    device: 'Desktop',
    browser: 'Firefox 132',
    os: 'Windows 11',
    sessionId: 'SESS-C4D9E1',
  },
  {
    id: 15,
    userId: 'SA-001',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    action: 'permission_change',
    module: 'security',
    targetName: 'María Gutiérrez (SA-002)',
    targetId: 'USR-SA-002',
    date: '2026-07-12 11:40:00',
    result: 'success',
    severity: 'warning',
    details: 'Ascendió rol de Admin a Admin Senior. Nuevos permisos: gestión de planes globales, acceso a billing, aprobación de frecuencias.',
    ipAddress: '190.124.45.10',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 15.2',
    sessionId: 'SESS-A7F3B2',
  },
  {
    id: 16,
    userId: 'SA-005',
    userName: 'Luis Hernández',
    userRole: 'Admin',
    action: 'critical_error',
    module: 'api',
    targetName: 'API Gateway — Rate Limit',
    targetId: 'SYS-API-001',
    date: '2026-07-12 12:05:33',
    result: 'failure',
    severity: 'critical',
    details: 'Rate limit excedido en API Gateway. 10,247 requests en 60s. Origen: cliente no autenticado. IP bloqueada temporalmente: 45.33.92.101.',
    ipAddress: '201.187.92.14',
    country: 'México',
    city: 'Guadalajara',
    device: 'Desktop',
    browser: 'Chrome 126',
    os: 'Windows 11',
    sessionId: 'SESS-F2A8D4',
  },
  {
    id: 17,
    userId: 'SA-001',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    action: 'config_change',
    module: 'settings',
    targetName: 'Configuración Global de Seguridad',
    targetId: 'SYS-SEC-001',
    date: '2026-07-12 12:15:00',
    result: 'success',
    severity: 'critical',
    details: 'Activó autenticación 2FA obligatoria para todos los admins. Aumentó timeout de sesión a 4 horas. Activó notificaciones de login sospechoso.',
    ipAddress: '190.124.45.10',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 15.2',
    sessionId: 'SESS-A7F3B2',
  },
  {
    id: 18,
    userId: 'SA-004',
    userName: 'Diana Ríos',
    userRole: 'Admin',
    action: 'bulk_operation',
    module: 'radios',
    targetName: 'Actualización masiva de almacenamiento',
    targetId: 'BULK-0042',
    date: '2026-07-12 12:30:45',
    result: 'success',
    severity: 'info',
    details: 'Aumentó storage a 50 GB para 15 radios del plan Free que excedieron el 90% de uso. Operación completada sin errores.',
    ipAddress: '190.124.45.41',
    country: 'Colombia',
    city: 'Bogotá',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 14.6',
    sessionId: 'SESS-D1F5A8',
  },
  {
    id: 19,
    userId: 'SA-003',
    userName: 'Andrés López',
    userRole: 'Moderador',
    action: 'logout',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 13:00:00',
    result: 'success',
    severity: 'info',
    details: 'Cierre de sesión manual. Sesión activa: 4h 15min.',
    ipAddress: '190.124.45.35',
    country: 'Argentina',
    city: 'Rosario',
    device: 'Desktop',
    browser: 'Edge 126',
    os: 'Windows 10',
    sessionId: 'SESS-B3E7C9',
  },
  {
    id: 20,
    userId: 'SA-007',
    userName: 'Pablo Montoya',
    userRole: 'Admin Técnico',
    action: 'login',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 13:15:00',
    result: 'success',
    severity: 'info',
    details: 'Inicio de sesión exitoso. Primera conexión del día.',
    ipAddress: '181.55.32.18',
    country: 'Chile',
    city: 'Santiago',
    device: 'Desktop',
    browser: 'Brave 1.68',
    os: 'Linux Ubuntu 24.04',
    sessionId: 'SESS-G6H2K9',
  },
  {
    id: 21,
    userId: 'SA-007',
    userName: 'Pablo Montoya',
    userRole: 'Admin Técnico',
    action: 'config_change',
    module: 'storage',
    targetName: 'CDN — Edge Configuration',
    targetId: 'CFG-CDN-003',
    date: '2026-07-12 13:30:00',
    result: 'success',
    severity: 'info',
    details: 'Configuró nuevo nodo CDN edge en São Paulo. Latencia reducida 40% para región LATAM sur.',
    ipAddress: '181.55.32.18',
    country: 'Chile',
    city: 'Santiago',
    device: 'Desktop',
    browser: 'Brave 1.68',
    os: 'Linux Ubuntu 24.04',
    sessionId: 'SESS-G6H2K9',
  },
  {
    id: 22,
    userId: 'SA-002',
    userName: 'María Gutiérrez',
    userRole: 'Admin Senior',
    action: 'approve',
    module: 'billing',
    targetName: 'Factura #INV-8842 — Radio Sonora Digital',
    targetId: 'INV-8842',
    date: '2026-07-12 14:00:00',
    result: 'success',
    severity: 'info',
    details: 'Aprobó factura por $49.00 correspondiente a renovación mensual Plan Premium. Período: Agosto 2026.',
    ipAddress: '190.124.45.22',
    country: 'Argentina',
    city: 'Córdoba',
    device: 'Desktop',
    browser: 'Firefox 132',
    os: 'Windows 11',
    sessionId: 'SESS-C4D9E1',
  },
  {
    id: 23,
    userId: 'SA-005',
    userName: 'Luis Hernández',
    userRole: 'Admin',
    action: 'export',
    module: 'analytics',
    targetName: 'Reporte Mensual — Julio 2026',
    targetId: 'RPT-JUL26',
    date: '2026-07-12 14:20:00',
    result: 'success',
    severity: 'info',
    details: 'Exportó reporte completo de analytics en formato Excel. 1,247 filas. Incluye métricas de 89 radios activas.',
    ipAddress: '201.187.92.14',
    country: 'México',
    city: 'Guadalajara',
    device: 'Desktop',
    browser: 'Chrome 126',
    os: 'Windows 11',
    sessionId: 'SESS-F2A8D4',
  },
  {
    id: 24,
    userId: 'SA-001',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    action: 'delete',
    module: 'content',
    targetName: 'Publicación #4581 — Radio Nueva Actitud',
    targetId: 'POST-4581',
    date: '2026-07-12 14:45:00',
    result: 'success',
    severity: 'warning',
    details: 'Eliminó publicación por infracción de derechos de autor. DMCA recibido. Contenido respaldado antes de eliminación.',
    ipAddress: '190.124.45.10',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 15.2',
    sessionId: 'SESS-A7F3B2',
  },
  {
    id: 25,
    userId: 'SA-006',
    userName: 'Sofía Vargas',
    userRole: 'Moderador',
    action: 'login',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 15:00:00',
    result: 'success',
    severity: 'info',
    details: 'Inicio de sesión exitoso desde IP verificada después del bloqueo temporal matutino.',
    ipAddress: '190.124.45.55',
    country: 'Argentina',
    city: 'Mendoza',
    device: 'Tablet',
    browser: 'Safari 18',
    os: 'iPadOS 18.4',
    sessionId: 'SESS-H8J1L3',
  },
  {
    id: 26,
    userId: 'SA-004',
    userName: 'Diana Ríos',
    userRole: 'Admin',
    action: 'create',
    module: 'notifications',
    targetName: 'Notificación masiva: Mantenimiento programado',
    targetId: 'NOTIF-0192',
    date: '2026-07-12 15:15:00',
    result: 'success',
    severity: 'warning',
    details: 'Creó notificación para todos los administradores de radio. Mantenimiento programado: 15/07/2026 02:00-04:00 UTC.',
    ipAddress: '190.124.45.41',
    country: 'Colombia',
    city: 'Bogotá',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 14.6',
    sessionId: 'SESS-D1F5A8',
  },
  {
    id: 27,
    userId: 'SA-001',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    action: 'feature_flag',
    module: 'feature_flags',
    targetName: 'dark_mode_dashboard',
    targetId: 'FF-0041',
    date: '2026-07-12 15:30:00',
    result: 'success',
    severity: 'info',
    details: 'Activó Feature Flag "Dark Mode Dashboard" para el 100% de usuarios admin. Lanzamiento general.',
    ipAddress: '190.124.45.10',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 15.2',
    sessionId: 'SESS-A7F3B2',
  },
  {
    id: 28,
    userId: 'SA-008',
    userName: 'Gabriela Fuentes',
    userRole: 'Auditor',
    action: 'login',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 16:00:00',
    result: 'success',
    severity: 'info',
    details: 'Inicio de sesión con rol de solo lectura Auditor. Acceso limitado a logs y reportes.',
    ipAddress: '200.68.19.42',
    country: 'Perú',
    city: 'Lima',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'Windows 11',
    sessionId: 'SESS-M4N6P8',
  },
  {
    id: 29,
    userId: 'SA-008',
    userName: 'Gabriela Fuentes',
    userRole: 'Auditor',
    action: 'export',
    module: 'security',
    targetName: 'Log de accesos — Julio 2026',
    targetId: 'AUDIT-JUL26',
    date: '2026-07-12 16:15:00',
    result: 'success',
    severity: 'info',
    details: 'Exportó log de auditoría completo del mes de julio en formato PDF. 3,892 registros.',
    ipAddress: '200.68.19.42',
    country: 'Perú',
    city: 'Lima',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'Windows 11',
    sessionId: 'SESS-M4N6P8',
  },
  {
    id: 30,
    userId: 'SA-007',
    userName: 'Pablo Montoya',
    userRole: 'Admin Técnico',
    action: 'critical_error',
    module: 'storage',
    targetName: 'Cluster Storage — Nodo 3',
    targetId: 'STG-N3',
    date: '2026-07-12 16:30:00',
    result: 'failure',
    severity: 'critical',
    details: 'Fallo en nodo de storage #3. Latencia > 5s. Failover automático a nodo #5. Investigación en curso.',
    ipAddress: '181.55.32.18',
    country: 'Chile',
    city: 'Santiago',
    device: 'Desktop',
    browser: 'Brave 1.68',
    os: 'Linux Ubuntu 24.04',
    sessionId: 'SESS-G6H2K9',
  },
  {
    id: 31,
    userId: 'SA-002',
    userName: 'María Gutiérrez',
    userRole: 'Admin Senior',
    action: 'permission_change',
    module: 'security',
    targetName: 'Luis Hernández (SA-005)',
    targetId: 'USR-SA-005',
    date: '2026-07-12 16:45:00',
    result: 'success',
    severity: 'warning',
    details: 'Restringió acceso a módulo de facturación. Motivo: revisión de seguridad. Acceso read-only temporal.',
    ipAddress: '190.124.45.22',
    country: 'Argentina',
    city: 'Córdoba',
    device: 'Desktop',
    browser: 'Firefox 132',
    os: 'Windows 11',
    sessionId: 'SESS-C4D9E1',
  },
  {
    id: 32,
    userId: 'SA-001',
    userName: 'Carlos Mendoza',
    userRole: 'Super Admin',
    action: 'logout',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 17:00:00',
    result: 'success',
    severity: 'info',
    details: 'Cierre de sesión manual. Sesión activa: 9h 15min. Todas las operaciones del día completadas.',
    ipAddress: '190.124.45.10',
    country: 'Argentina',
    city: 'Buenos Aires',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 15.2',
    sessionId: 'SESS-A7F3B2',
  },
  {
    id: 33,
    userId: 'SA-004',
    userName: 'Diana Ríos',
    userRole: 'Admin',
    action: 'edit',
    module: 'integrations',
    targetName: 'Integración: Stripe',
    targetId: 'INTG-STRIPE-01',
    date: '2026-07-12 17:15:00',
    result: 'success',
    severity: 'info',
    details: 'Actualizó webhook endpoint de Stripe. Nueva URL configurada. Test de conexión exitoso.',
    ipAddress: '190.124.45.41',
    country: 'Colombia',
    city: 'Bogotá',
    device: 'Desktop',
    browser: 'Chrome 127',
    os: 'macOS 14.6',
    sessionId: 'SESS-D1F5A8',
  },
  {
    id: 34,
    userId: 'SA-006',
    userName: 'Sofía Vargas',
    userRole: 'Moderador',
    action: 'bulk_operation',
    module: 'moderation',
    targetName: 'Limpieza masiva de mensajes',
    targetId: 'BULK-0043',
    date: '2026-07-12 17:30:00',
    result: 'success',
    severity: 'warning',
    details: 'Eliminó 47 mensajes de spam en 3 cabinas diferentes. Patrón detectado: misma IP de origen. IP bloqueada permanentemente.',
    ipAddress: '190.124.45.55',
    country: 'Argentina',
    city: 'Mendoza',
    device: 'Tablet',
    browser: 'Safari 18',
    os: 'iPadOS 18.4',
    sessionId: 'SESS-H8J1L3',
  },
  {
    id: 35,
    userId: 'SA-007',
    userName: 'Pablo Montoya',
    userRole: 'Admin Técnico',
    action: 'logout',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 17:45:00',
    result: 'success',
    severity: 'info',
    details: 'Cierre de sesión automático por timeout. Inactividad: 45 minutos.',
    ipAddress: '181.55.32.18',
    country: 'Chile',
    city: 'Santiago',
    device: 'Desktop',
    browser: 'Brave 1.68',
    os: 'Linux Ubuntu 24.04',
    sessionId: 'SESS-G6H2K9',
  },
  {
    id: 36,
    userId: 'SA-002',
    userName: 'María Gutiérrez',
    userRole: 'Admin Senior',
    action: 'logout',
    module: 'auth',
    targetName: 'Sistema',
    targetId: '—',
    date: '2026-07-12 18:00:00',
    result: 'success',
    severity: 'info',
    details: 'Cierre de sesión manual. Sesión activa: 9h 50min.',
    ipAddress: '190.124.45.22',
    country: 'Argentina',
    city: 'Córdoba',
    device: 'Desktop',
    browser: 'Firefox 132',
    os: 'Windows 11',
    sessionId: 'SESS-C4D9E1',
  },
];

export const userOptions = [
  { id: 'SA-001', name: 'Carlos Mendoza', role: 'Super Admin' },
  { id: 'SA-002', name: 'María Gutiérrez', role: 'Admin Senior' },
  { id: 'SA-003', name: 'Andrés López', role: 'Moderador' },
  { id: 'SA-004', name: 'Diana Ríos', role: 'Admin' },
  { id: 'SA-005', name: 'Luis Hernández', role: 'Admin' },
  { id: 'SA-006', name: 'Sofía Vargas', role: 'Moderador' },
  { id: 'SA-007', name: 'Pablo Montoya', role: 'Admin Técnico' },
  { id: 'SA-008', name: 'Gabriela Fuentes', role: 'Auditor' },
];

export const radioOptions = [
  'Radio Vibra FM',
  'Radio Élite Digital',
  'Radio Sonora Digital',
  'Radio Nueva Actitud',
  'Radio Urbana 101',
  'TechBeats',
  'Radio Deportiva Total',
  'Radio Marítima',
];